import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, type FormEvent } from 'react'
import Panel from '../../components/cards/Panel'
import { apiClient, normalizeApiError } from '../../lib/apiClient'
import type { ApiResponse } from '../../types'

type SettingRow = { id: number; setting_key: string; setting_value: string | null }
const identityFields = [
  ['site_name', 'Website name', 'Online Doctor Appointment'],
  ['site_tagline', 'Short tagline', 'Healthcare anytime, anywhere'],
  ['about_title', 'About heading', 'About us'],
  ['about_content', 'About the person or organization', 'Describe the person, clinic, hospital or organization that owns this website.'],
  ['footer_text', 'Footer text', 'Online consultation is not an emergency service.'],
] as const

export default function SiteIdentitySettings() {
  const form = useRef<HTMLFormElement>(null)
  const cache = useQueryClient()
  const query = useQuery({ queryKey: ['public-settings'], queryFn: async () => (await apiClient.get<ApiResponse<Record<string, string>>>('/public/settings')).data.data })
  const save = useMutation({
    mutationFn: async (values: Record<string, string>) => Promise.all(identityFields.map(async ([key]) => {
      const matches = (await apiClient.get<ApiResponse<SettingRow[]>>('/admin/settings', { params: { search: key } })).data.data
      const existing = matches.find(row => row.setting_key === key)
      const payload = { setting_group: 'site', setting_key: key, setting_value: values[key], is_public: true }
      return existing ? apiClient.put(`/admin/settings/${existing.id}`, payload) : apiClient.post('/admin/settings', payload)
    })),
    onSuccess: () => { void query.refetch(); void cache.invalidateQueries({ queryKey: ['public-settings'] }); void cache.invalidateQueries({ queryKey: ['portfolio'] }) },
  })
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    save.mutate(Object.fromEntries(new FormData(event.currentTarget)) as Record<string, string>)
  }
  return <Panel title="Website identity" subtitle="White-label this software for any doctor, clinic, hospital or organization.">
    {query.isLoading ? <p role="status">Loading website settings…</p> : query.isError ? <p role="alert">Unable to load website settings.</p> : <form ref={form} onSubmit={submit}>
      <div className="form-grid">{identityFields.map(([key, label, placeholder]) => <label key={key}>{label}{key === 'about_content' ? <textarea className="form-control" name={key} rows={5} defaultValue={query.data?.[key] ?? ''} placeholder={placeholder} /> : <input className="form-control" name={key} required={key === 'site_name'} defaultValue={query.data?.[key] ?? ''} placeholder={placeholder} />}</label>)}</div>
      {save.isError && <p className="alert alert-danger" role="alert">{normalizeApiError(save.error).message}</p>}
      {save.isSuccess && <p className="alert alert-success" role="status">Website identity saved and published.</p>}
      <button className="btn btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save website identity'}</button>
    </form>}
  </Panel>
}
